import React, { useState } from 'react';
import { Eye, Download, FileText } from 'lucide-react';
import ImageCarouselModal from './ImageCarouselModal';

function DriverDeliveriesTable({ deliveries, onExport, showUserColumn }) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [showModal, setShowModal] = useState(false);

  function handleViewImages(images) {
    setSelectedImages(images);
    setShowModal(true);
  }

  function truncateName(name) {
    if (!name) return '-';
    return name.length > 10 ? name.substring(0, 10) + '...' : name;
  }

  function getStatusBadge(status) {
    const statusConfig = {
      entregado: {
        label: 'Entregado',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        borderColor: 'border-green-300'
      },
      pending: {
        label: 'Pendiente',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-300'
      },
      cancelado: {
        label: 'Cancelado',
        bgColor: 'bg-slate-100',
        textColor: 'text-slate-700',
        borderColor: 'border-slate-300'
      },
      rechazado: {
        label: 'Rechazado',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        borderColor: 'border-red-300'
      },
      pospuesto: {
        label: 'Pospuesto',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-300'
      },
      encamino: {
        label: 'En Camino',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-300'
      }
    };

    const normalizedStatus = status ? status.toLowerCase() : 'pending';
    const config = statusConfig[normalizedStatus] || statusConfig.pending;

    return (
      <span className={`
        inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold font-montserrat
        ${config.bgColor} ${config.textColor} border ${config.borderColor}
      `}>
        {config.label}
      </span>
    );
  }

  if (deliveries.length === 0) {
    return (
      <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center">
        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 font-montserrat">
          No hay entregas para mostrar
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
      {/* Export Button */}
      <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
        <h3 className="font-bold text-slate-800 font-montserrat">
          Entregas ({deliveries.length})
        </h3>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-all font-montserrat font-semibold"
        >
          <Download className="w-4 h-4" />
          Exportar Excel
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">ID</th>
              {showUserColumn && (
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Usuario</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Estatus</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Destinatario</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Notas</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Cargo</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Cobrado</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">FastMax</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Proveedor</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Vuelto</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 font-montserrat">Imágenes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {deliveries.map((delivery) => (
              <tr key={delivery.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 text-sm text-slate-800 font-montserrat font-semibold">
                  #{delivery.id}
                </td>
                {showUserColumn && (
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    {delivery.user_name || '-'}
                  </td>
                )}
                <td className="px-4 py-3">
                  {getStatusBadge(delivery.status)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-800 font-montserrat" title={delivery.recipient_name}>
                  {truncateName(delivery.recipient_name)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 font-montserrat max-w-xs truncate" title={delivery.notes_driver}>
                  {delivery.notes_driver ? (delivery.notes_driver.length > 18 ? delivery.notes_driver.substring(0, 18) + '...' : delivery.notes_driver) : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-slate-800 font-montserrat font-semibold">
                  S/. {(delivery.charge || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-brand-primary font-montserrat font-bold">
                  S/. {(delivery.charged || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-brand-gold font-montserrat font-bold">
                  S/. {(delivery.fastmax_transfer_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-slate-600 font-montserrat font-semibold">
                  S/. {(delivery.provider_transfer_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-slate-600 font-montserrat font-semibold">
                  S/. {(delivery.fastmax_exchange_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-center">
                  {delivery.image_urls && delivery.image_urls.length > 0 ? (
                    <button
                      onClick={() => handleViewImages(delivery.image_urls)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-primary/10 text-brand-primary rounded-lg hover:bg-brand-primary/20 transition-all font-montserrat font-semibold text-xs"
                    >
                      <Eye className="w-4 h-4" />
                      {delivery.image_urls.length}
                    </button>
                  ) : (
                    <span className="text-slate-400 text-xs font-montserrat">Sin imágenes</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Image Modal */}
      {showModal && (
        <ImageCarouselModal
          images={selectedImages}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default DriverDeliveriesTable;